/** @file briefLine: one art brief line: file name, exact size, what to draw, where the joint is. */
import { bandWords } from './bandWords';
import { facingWords } from './facingWords';
import { partName } from './partName';
import { slotFileName } from './slotFileName';
import type { ViewedSlot } from './artTypes';

/** A pixel coordinate without float noise: `8`, `9.5`. */
const px = (n: number): string => String(Math.round(n * 100) / 100);

/** The facing for the slot: the view's body azimuth when the server reported one, else the tile's drawn azimuth. */
function facingOf(slot: ViewedSlot): number | undefined {
  return slot.view?.bodyAzimuthDeg ?? slot.drawnAzimuth;
}

/**
 * The line for `slot`: ``- `face.0.4.png` 16x16: face, <facing>, <band>[, frames][, variants]; the joint is at pixel x,
 * y from the top-left``; a tile that repeats another flipped is ``- `a.png` (mirrored from `b.png`; do not draw)``.
 */
export function briefLine(slot: ViewedSlot, cameraPitchDeg: number | null = null): string {
  if (slot.mirroredFrom !== undefined) {
    return `- \`${slotFileName(slot.key)}\` (mirrored from \`${slotFileName(slot.mirroredFrom)}\`; do not draw)`;
  }
  const what = [partName(slot.drawable), facingWords(facingOf(slot)), bandWords(slot.bands)];
  if (slot.view !== undefined && slot.view.elevationDeg !== cameraPitchDeg) what.push(`seen from ${px(slot.view.elevationDeg)} deg above`);
  if (slot.frames.length > 0) what.push(`animation frame ${slot.frames.join(', ')}`);
  if (slot.variants.length > 0) what.push(`variant ${slot.variants.join(' + ')}`);
  const [w, h] = slot.size;
  return `- \`${slotFileName(slot.key)}\` ${String(w)}x${String(h)}: ${what.join(', ')}; the joint is at pixel ${px(slot.pivot[0])}, ${px(slot.pivot[1])} from the top-left`;
}
